import React, { useState, useRef, isValidElement, useEffect, } from 'react';
import { arrayOf, bool, func, node, number, object, oneOfType, shape, string, } from 'prop-types';
import Select, { components, createFilter } from 'react-select';
import { Flex } from '../layout';
import Checkbox from '../checkbox';
import Chip from '../chip';
import Button from '../button';
import setClass from '../../common/set-class';
import useOutsideClick from '../../hooks/use-outside-click';
import Icon from '../icon';
import { ColorRed50 } from '../../tokens/colors/colors';
const DropdownIndicator = (props) => {
    const { selectProps: { inputValue, clearSearchValue }, } = props;
    const isSearching = typeof inputValue !== 'string' || inputValue !== '';
    return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(components.DropdownIndicator, { ...props }, isSearching ? (React.createElement("button", { type: "button", onClick: clearSearchValue },
        React.createElement(Icon, { name: "circle-xmark", className: "b-select__icon b-select__icon--search" }))) : (React.createElement(Icon, { name: "magnifying-glass", className: "b-select__icon b-select__icon--search" }))));
};
const Option = (props) => {
    const { selectOption, data, selectProps: { value: values }, } = props;
    const isSelected = !!values.find(({ value }) => value === data.value);
    const renderCustom = () => {
        const { render } = data;
        if (!render) {
            return null;
        }
        if (typeof render === 'string' || isValidElement(render)) {
            return React.createElement("div", { onClick: () => selectOption(data) }, render);
        }
        if (typeof render === 'number') {
            return React.createElement("div", { onClick: () => selectOption(data) }, String(render));
        }
        if (typeof render === 'function') {
            return render({
                option: data,
                values,
                isSelected,
            });
        }
        return null;
    };
    return (React.createElement(Flex, { align: "center", className: setClass([
            'b-select-multi__option-container',
            isSelected ? 'b-select-multi__option-container--selected' : '',
        ]) },
        React.createElement(Checkbox, { checked: isSelected, onChange: () => selectOption(data) }),
        React.createElement(components.Option, { ...props })));
};
const Menu = (props) => {
    const { children } = props;
    return React.createElement("div", { className: "b-select-multi__menu" }, children);
};
const AddNewButton = ({ addNewText, handleAddNew, }) => (React.createElement(Button, { className: "b-select-multi__add-new-button", kind: "link", icon: "circle-plus", size: "small", onClick: handleAddNew }, addNewText));
const MenuList = (props) => {
    const { children, options, selectOption, setValue, getValue, hasValue, clearValue, selectProps: { value: values, isDisabled: disabled, menuIsOpen: isOpen, onChange, onInputChange, inputValue, isMenuListHidden, addNewEnabled, addNewText, onAddNew, bottomControl, setIsOpen, clearSearchValue, }, } = props;
    const handleAddNew = (e) => {
        const cleanUpOpts = {
            isCloseMenu: true,
            isClearSearchValue: true,
        };
        const keepMenuOpen = () => {
            cleanUpOpts.isCloseMenu = false;
        };
        const keepSearchValue = () => {
            cleanUpOpts.isClearSearchValue = false;
        };
        onAddNew(e, { keepMenuOpen, keepSearchValue });
        const addNewSelectSideEffects = [
            cleanUpOpts.isCloseMenu && (() => setIsOpen(false)),
            cleanUpOpts.isClearSearchValue && (() => clearSearchValue()),
        ];
        // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
        addNewSelectSideEffects.filter(Boolean).forEach((effect) => effect());
    };
    const renderBottomControl = () => {
        if (!bottomControl && addNewEnabled) {
            return (React.createElement(AddNewButton, { addNewText: addNewText, handleAddNew: handleAddNew }));
        }
        if (!bottomControl) {
            return null;
        }
        if (typeof bottomControl === 'function') {
            return bottomControl({
                values,
                inputValue,
                disabled,
                isOpen,
                onChange,
                onInputChange,
                options,
                selectOption,
                setValue,
                getValue,
                hasValue,
                clearValue,
                clearSearchValue,
                setIsOpen,
                addNewButton: addNewEnabled ? (React.createElement(AddNewButton, { addNewText: addNewText, handleAddNew: handleAddNew })) : null,
            });
        }
        return bottomControl;
    };
    const BottomControl = renderBottomControl();
    return (React.createElement("div", { className: "select-multi-search__menu-list" },
        !isMenuListHidden && (React.createElement(components.MenuList, { ...props }, children)),
        BottomControl && !isMenuListHidden && (React.createElement("div", { className: setClass('b-select-multi__bottom-control', addNewEnabled && 'b-select-multi__add-new') }, BottomControl))));
};
const Blanket = (props) => {
    const { onClick } = props;
    return React.createElement("div", { tabIndex: 0, onClick: onClick });
};
const ValueContainer = (props) => {
    const { disabled, isOpen, onRemoveValue, placeholder, displayCustomValue, toggleOpen, values, options, errorText, } = props;
    const noValues = values?.length <= 0;
    const hasDisplayCustomValue = typeof displayCustomValue === 'function';
    const handleRemoveValue = (e, option, allValues) => {
        e.stopPropagation();
        onRemoveValue(option, allValues);
    };
    const renderValues = (customValue) => {
        if (customValue) {
            return (React.createElement("div", { className: "b-txt b-select-multi-search__custom-value" }, customValue));
        }
        if (!values || noValues) {
            return (React.createElement("p", { className: "b-select-multi-search__inactive-msg" }, placeholder));
        }
        return values.map(({ label, value }) => 
        // Do not render the "Select all (x)" chip
        value === 'select-all' ? null : (React.createElement("div", { key: `b-select-multi-search__option-${value}`, className: "b-select-multi__value-remove" },
            React.createElement(Chip, { onRightIconClick: (e) => handleRemoveValue(e, { value, label }, values), kind: "secondary", iconRight: disabled ? '' : 'xmark', text: label, className: "b-select-multi__value" }))));
    };
    const renderValueContainer = () => {
        const customValue = hasDisplayCustomValue
            ? displayCustomValue({
                disabled,
                isOpen,
                placeholder,
                errorText,
                options,
                values,
            })
            : null;
        return (React.createElement(Flex, { justify: "flex-start", align: "center", className: setClass([
                'b-select-multi__value-container',
                disabled ? 'b-select-multi__value-container--disabled' : '',
                isOpen ? 'b-select-multi__value-container--active' : '',
                !noValues && !customValue
                    ? 'b-select-multi__value-container-filled'
                    : '',
                errorText && '--error',
            ]), wrap: true },
            renderValues(customValue),
            React.createElement(Icon, { name: "angle-down", size: "sm", className: setClass([
                    'b-select__icon',
                    isOpen ? '' : 'b-select__icon--closed',
                ]) })));
    };
    return (React.createElement("div", { tabIndex: 0, className: "b-select-multi__value-container-toggle", onClick: toggleOpen }, renderValueContainer()));
};
const Dropdown = (props) => {
    const { children, disabled, isOpen, onDropdownClose, onRemoveValue, placeholder, displayCustomValue, toggleOpen, values, options, errorText, } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement(ValueContainer, { disabled: disabled, isOpen: isOpen, onRemoveValue: onRemoveValue, placeholder: placeholder, displayCustomValue: displayCustomValue, toggleOpen: toggleOpen, values: values, options: options, errorText: errorText }),
        isOpen && !disabled ? React.createElement(Menu, null, children) : null,
        isOpen && !disabled ? React.createElement(Blanket, { onClick: onDropdownClose }) : null));
};
const Control = (props) => {
    const { isSearchable, isMenuListHidden, topControl } = props.selectProps;
    if (isSearchable || topControl) {
        return (React.createElement("div", { className: setClass(topControl ? 'b-select__control-with-top-control' : '') },
            topControl && (React.createElement("div", { className: setClass('b-select__top-control', isMenuListHidden ? 'only-item' : '') }, topControl)),
            isSearchable && !isMenuListHidden && React.createElement(components.Control, { ...props })));
    }
    else
        return null;
};
const MultiSelectSearch = (props) => {
    const { addNewEnabled, addNewText, bottomControl, className, compact, auto, disabled, extra, isInitialOpen, onAddNew, onChange, onClose, options, placeholder, searchPlaceholder, displayCustomValue, topControl, hasSelectAll, customSelectAllText, isMenuListHidden, style, testId, values, errorText, isSearchable, formatOptionLabel, getOptionLabel, getOptionValue, filterOption, name, } = props;
    const [isOpen, setIsOpen] = useState(isInitialOpen);
    const [inputValue, setInputValue] = useState('');
    const [isSelectAllSelected, setIsSelectAllSelected] = useState(false);
    const wrapperRef = useRef(null);
    const selectAllOption = {
        label: customSelectAllText || `Select all (${options.length})`,
        value: 'select-all',
    };
    const optionsWithSelectAll = [
        // Only apply the "Select all" option if there are options to choose from
        ...(options.length ? [selectAllOption] : []),
        ...options,
    ];
    const valuesWithSelectAll = [
        ...(isSelectAllSelected ? [selectAllOption] : []),
        ...values,
    ];
    // Update whether or not "Select all" should be selected each time either `options` or `values` changes
    useEffect(() => {
        // For "Select all" to be checked, options must be available and all must be selected
        const hasOptions = options.length > 0;
        const allOptionsAreSelected = values.length === options.length;
        setIsSelectAllSelected(hasOptions && allOptionsAreSelected);
    }, [options, values]);
    // eslint-disable-next-line consistent-return
    const handleOnClose = async (isSelectOpen) => {
        if (isSelectOpen) {
            await new Promise((resolve) => {
                setInputValue('');
                onClose();
                // @ts-expect-error ts-migrate(2794) FIXME: Expected 1 arguments, but got 0. Did you forget to... Remove this comment to see the full error message
                resolve();
            });
            return setIsOpen(false);
        }
    };
    useOutsideClick(wrapperRef, (e) => {
        if (!e.composedPath().includes(wrapperRef?.current)) {
            handleOnClose(isOpen);
        }
    }, 
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'any' is not assignable to type 'never'.
    [isOpen]);
    const toggleOpen = () => {
        if (!disabled) {
            handleOnClose(isOpen);
            setIsOpen((prevToggleVal) => !prevToggleVal);
        }
    };
    const onSelectChange = (selectedValues, actionObj) => {
        if (!hasSelectAll) {
            onChange(selectedValues, actionObj, {
                options,
                inputValue,
                isOpen,
            });
        }
        else {
            // Perform additional logic if "Select all" is enabled
            const { action, option } = actionObj;
            let modifiedSelectedValues = selectedValues;
            if (action === 'select-option') {
                // If "Select all" is selected, all options are selected
                if (option.value === selectAllOption.value) {
                    setIsSelectAllSelected(true);
                    modifiedSelectedValues = options;
                }
                else if (selectedValues.length === options.length) {
                    // If all the items are individually selected, we manually select the "Select all" option
                    setIsSelectAllSelected(true);
                }
            }
            else {
                // If "Select all" is unselected, all options are unselected
                if (option.value === selectAllOption.value) {
                    setIsSelectAllSelected(false);
                    modifiedSelectedValues = [];
                }
                else if (isSelectAllSelected) {
                    // If an individual item was unselected while "Select all" was selected, manually unselect "Select all"
                    setIsSelectAllSelected(false);
                    modifiedSelectedValues = selectedValues.filter((item) => item.value !== selectAllOption.value);
                }
            }
            onChange(modifiedSelectedValues, actionObj, {
                options,
                inputValue,
                isOpen,
            });
        }
    };
    const onRemoveValue = (optionRemove, allValues) => {
        const valuesChange = allValues.filter(({ value }) => value !== optionRemove.value);
        // Since a value is being deselected, ensure "Select all" is set to false
        if (hasSelectAll) {
            setIsSelectAllSelected(false);
        }
        onChange(valuesChange, {
            action: 'deselect-option',
            option: optionRemove,
        }, { options, inputValue, isOpen });
    };
    return (React.createElement("div", { className: setClass([
            'b-select-multi__wrapper',
            className,
            compact && 'b-select--max-width',
            auto && '--auto',
        ]), ref: wrapperRef, "data-testid": testId },
        React.createElement(Dropdown, { disabled: disabled, isOpen: isOpen, onDropdownClose: toggleOpen, onRemoveValue: onRemoveValue, placeholder: placeholder, displayCustomValue: displayCustomValue, toggleOpen: toggleOpen, options: options, values: values, errorText: errorText },
            React.createElement(Select, { className: "b-select-multi-menu", classNamePrefix: "b-select", inputId: `b-select-multi-menu-input--${name}`, instanceId: `b-select-multi-menu-component-instance--${name}`, name: name, components: {
                    DropdownIndicator,
                    MenuList,
                    Option,
                    ClearIndicator: null,
                    Control,
                }, clearSearchValue: () => setInputValue(''), inputValue: inputValue, options: hasSelectAll ? optionsWithSelectAll : options, placeholder: searchPlaceholder, isSearchable: isSearchable, isMenuListHidden: isMenuListHidden, addNewEnabled: addNewEnabled, addNewText: addNewText, topControl: topControl, bottomControl: bottomControl, onAddNew: onAddNew, onChange: onSelectChange, onInputChange: (value, { action }) => {
                    if (action === 'input-change') {
                        setInputValue(value);
                    }
                }, value: hasSelectAll ? valuesWithSelectAll : values, noOptionsMessage: () => (React.createElement("p", { className: "b-select-multi-search__no-results-msg" }, "No results found")), styles: style, isDisabled: disabled, controlShouldRenderValue: false, hideSelectedOptions: false, backspaceRemovesValue: false, isMulti: true, menuIsOpen: true, autoFocus: true, setIsOpen: setIsOpen, formatOptionLabel: formatOptionLabel, getOptionLabel: getOptionLabel, getOptionValue: getOptionValue, filterOption: filterOption, ...extra })),
        errorText && (React.createElement("div", { className: "b-flex b-mt-tiny b-flex-align-center b-pos-abs" },
            React.createElement(Icon, { name: "triangle-exclamation", color: ColorRed50, className: "b-text-input__err-icon" }),
            React.createElement("p", { className: "b-txt b-txt--error" },
                " ",
                errorText,
                " ")))));
};
DropdownIndicator.propTypes = {
    selectProps: shape({
        inputValue: string,
        clearSearchValue: func,
    }),
};
DropdownIndicator.defaultProps = {
    selectProps: {
        inputValue: '',
        clearSearchValue: () => { },
    },
};
Option.propTypes = {
    data: shape({ label: string, value: oneOfType([string, number]) }),
    selectOption: func,
    selectProps: shape({
        value: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    }),
};
Option.defaultProps = {
    data: {},
    selectOption: () => { },
    selectProps: { value: [] },
};
MenuList.propTypes = {
    children: node,
    selectProps: shape({
        addNewEnabled: bool,
        addNewText: string,
        onAddNew: func,
        bottomControl: oneOfType([node, func]),
    }),
};
MenuList.defaultProps = {
    children: React.createElement(React.Fragment, null),
    selectProps: {
        addNewEnabled: false,
        addNewText: 'Add New',
        onAddNew: () => { },
        bottomControl: null,
    },
};
Menu.propTypes = {
    children: node,
};
Menu.defaultProps = {
    children: React.createElement(React.Fragment, null),
};
Blanket.propTypes = {
    onClick: func,
};
Blanket.defaultProps = {
    onClick: () => { },
};
ValueContainer.propTypes = {
    disabled: bool,
    isOpen: bool,
    onRemoveValue: func,
    placeholder: string,
    displayCustomValue: func,
    toggleOpen: func,
    options: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    values: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    errorText: oneOfType([string, bool]),
};
ValueContainer.defaultProps = {
    disabled: false,
    isOpen: false,
    onRemoveValue: () => { },
    placeholder: 'Select Option',
    displayCustomValue: null,
    toggleOpen: () => { },
    options: [],
    values: [],
    errorText: false,
};
Dropdown.propTypes = {
    children: node,
    disabled: bool,
    isOpen: bool,
    onDropdownClose: func,
    onRemoveValue: func,
    placeholder: string,
    displayCustomValue: func,
    toggleOpen: func,
    options: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    values: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    errorText: oneOfType([string, bool]),
};
Dropdown.defaultProps = {
    children: React.createElement(React.Fragment, null),
    disabled: false,
    isOpen: false,
    onDropdownClose: () => { },
    onRemoveValue: () => { },
    placeholder: 'Select Option',
    displayCustomValue: null,
    toggleOpen: () => { },
    options: [],
    values: [],
    errorText: false,
};
MultiSelectSearch.propTypes = {
    addNewEnabled: bool,
    addNewText: string,
    bottomControl: oneOfType([node, func]),
    className: string,
    compact: bool,
    auto: bool,
    disabled: bool,
    extra: object,
    isInitialOpen: bool,
    onAddNew: func,
    onChange: func,
    onClose: func,
    options: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    placeholder: string,
    searchPlaceholder: string,
    isSearchable: bool,
    displayCustomValue: func,
    topControl: node,
    hasSelectAll: bool,
    customSelectAllText: string,
    isMenuListHidden: bool,
    style: object,
    testId: string,
    values: arrayOf(shape({ label: string, value: oneOfType([string, number]) })),
    errorText: oneOfType([string, bool]),
    formatOptionLabel: func,
    getOptionLabel: func,
    getOptionValue: func,
    filterOption: func,
    name: string,
};
MultiSelectSearch.defaultProps = {
    addNewEnabled: false,
    addNewText: 'Add New',
    bottomControl: null,
    className: null,
    compact: false,
    auto: false,
    disabled: false,
    extra: {},
    isInitialOpen: false,
    onAddNew: () => { },
    onChange: () => { },
    onClose: () => { },
    options: [],
    placeholder: 'Select Option',
    searchPlaceholder: 'Search Blueprint',
    isSearchable: true,
    displayCustomValue: null,
    topControl: null,
    hasSelectAll: false,
    customSelectAllText: null,
    isMenuListHidden: false,
    style: {},
    testId: undefined,
    values: [],
    errorText: false,
    formatOptionLabel: undefined,
    getOptionLabel: undefined,
    getOptionValue: undefined,
    filterOption: undefined,
    name: 'default',
};
export { createFilter };
export default MultiSelectSearch;
